'use client';

import { ChevronDownIcon } from 'lucide-react';
import { type Locale, useLocale, useTranslations } from 'next-intl';
import { useTransition } from 'react';

import Globe02 from '@ui/components/icons/Globe02';
import { Button } from '@ui/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@ui/components/ui/dropdown-menu';
import { LOCALES } from '@website/constants';
import { usePathname, useRouter } from '@website/i18n/navigation';
import { setLocaleCookieClient } from '@website/utils/localeCookie';

export function FooterLanguageSwitcher() {
  const t = useTranslations('Footer');
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const [isPending, startTransition] = useTransition();

  const locales = LOCALES;

  function onSelectChange(nextLocale: string) {
    // Save locale preference to cookie before navigation
    setLocaleCookieClient(nextLocale);
    startTransition(() => {
      router.replace(pathname, { locale: nextLocale as Locale });
    });
  }

  // Get the current locale's full label (e.g., "English" instead of "en")
  const currentLocale = locales.find((loc) => loc.code === locale);
  const currentLanguageName = currentLocale?.label || locale;

  return (
    <div className="flex flex-col items-start gap-5 min-w-3xs">
      <div className="text-neutral-700 text-2xl font-semibold font-manrope leading-loose">{t('language')}</div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={isPending}>
          <Button variant="outline" size="default" className="cursor-pointer w-full sm:w-auto justify-between">
            <div className="flex items-center gap-2">
              <Globe02 className="h-5 w-5" />
              <span>{currentLanguageName}</span>
            </div>
            <ChevronDownIcon className="ml-2 h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" style={{ width: 'var(--radix-dropdown-menu-trigger-width)' }}>
          {locales.map((loc) => (
            <DropdownMenuItem
              key={loc.code}
              onClick={() => onSelectChange(loc.code)}
              disabled={isPending || locale === loc.code}
              className={locale === loc.code ? 'font-medium' : 'cursor-pointer'}
            >
              {loc.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
